import { FrameworkControlOverviewViewModel, FrameworkControlTaskRow, FrameworkControlTaskTableRow } from "./FrameworkControlOverview.types";
import { useParams } from "react-router";
import { usePanes, useRefetchWhenPaneSaves } from "../../../main/panes/usePanes";
import ActivityPane from "../../../annualWheel/activityPane/ActivityPane";
import { useTaskUrlBinding } from "../../../taskManagement/useTaskUrlBinding";
import { put } from "../../../common/api/apiShared";
import { useState } from "react";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { useTranslation } from "../../../localization/useTranslation";
import { useOptimisticUpdate } from "../../../common/hooks/useOptimisticUpdate";
import { SelectableItem } from "../../../processingActivity/ProcessingActivity.types";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { filterFrameworkControlTaskRows, monthKey } from "./FrameworkControlOverview.utils";

export default function useFrameworkControlOverviewHooks() {
    const { id } = useParams<{ id: string }>();
    const { translateString, translateDateWithFullMonthAndYear, translateFrameworkControlStatusEnum, translateEvaluationEnum } = useTranslation();
    const responsibleQuery = useResponsibleQuery();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();

    const url = `/frameworkControls/${id}/overview`;
    const { isLoading, data, refetch } = useDotLegalQuery<FrameworkControlOverviewViewModel>(url);

    const [searchString, setSearchString] = useStateSessionStorage<string>(`frameworkControlOverviewSearch:${id}`, "");
    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(`frameworkControlOverviewFilter:${id}`);

    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [taskForDeletion, setTaskForDeletion] = useState<FrameworkControlTaskTableRow | undefined>(undefined);
    const { open: openPane } = usePanes();
    const [showAddAnnualWheelActivityDialog, setShowAddAnnualWheelActivityDialog] = useState(false);
    const [showAddFrameworkControlTemplateAnnualWheelActivity, setShowAddFrameworkControlTemplateAnnualWheelActivity] = useState(false);

    useTaskUrlBinding();

    useRefetchWhenPaneSaves(refetch);

    function openActivity(annualWheelActivityId: string) {
        openPane(ActivityPane, { mode: "edit", annualWheelActivityId, startDate: new Date() });
    }

    const statusOptions = data?.statusOptions?.map((x) => ({ id: x.toString(), name: translateFrameworkControlStatusEnum(x.toString()) }));

    // The backend returns one deadline per day, so several of them collapse into the same month. Duplicate ids
    // would give the popover duplicate rows and make a single pick render as a count instead of the month name.
    const deadlineOptions = data
        ? [...new Map(data.deadlineOptions.map((x) => [monthKey(x), { id: monthKey(x), name: translateDateWithFullMonthAndYear(x) }])).values()]
        : undefined;

    const filteredTableData = filterFrameworkControlTaskRows(formatDataToTable(data?.rows ?? []), selectFilters, filterOperators);
    const hasActiveFilters = Object.values(selectFilters).some((values) => values.length > 0);

    const updateTaskResponsibleMutation = useDotLegalMutation(updateResponsible);
    const updateAssigneeMutation = useDotLegalMutation(updateAssignee);

    async function onResponsibleChange(taskId: string, responsibleId: string) {
        let temp = { ...data! };
        let control = temp.rows.find((x) => x.taskId === taskId);
        let user = responsibleQuery.userData?.find((x) => x.id === responsibleId);
        control!.responsible = { id: responsibleId, name: user!.name };

        temp.userOptions = getUpdatedUserOptions(temp);

        putOnQueueAndSetQueryData(temp, url, updateTaskResponsibleMutation.mutateAsync, { taskId: taskId, responsibleId: responsibleId });
    }

    async function onAssigneesChange(taskId: string, assigneeIds: Array<string>) {
        let temp = { ...data! };
        let control = temp.rows.find((x) => x.taskId === taskId);
        let assignees = assigneeIds.map((id) => responsibleQuery.userData!.find((r) => r.id === id));
        control!.assignees = assignees!.map((x) => ({
            id: x!.id,
            name: x!.name,
        }));

        temp.userOptions = getUpdatedUserOptions(temp);

        putOnQueueAndSetQueryData(temp, url, updateAssigneeMutation.mutateAsync, { taskId: taskId, assigneeIds: assigneeIds });
    }

    function getUpdatedUserOptions(data: FrameworkControlOverviewViewModel) {
        let userOptions: Array<SelectableItem> = [];
        data.rows.forEach((x) => {
            userOptions = userOptions.concat(x.assignees.map((x) => ({ id: x!.id, name: x!.name })));

            if (x.responsible) {
                userOptions.push({ id: x.responsible.id, name: x.responsible.name });
            }
        });

        return [...new Map(userOptions.map((item) => [item.id, item])).values()].sort((a, b) => a.name.localeCompare(b.name));
    }

    const onAddedAnnualWheelActivityTemplates = () => {
        setShowAddFrameworkControlTemplateAnnualWheelActivity(false);
        refetch();
    };

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        id,
        data,
        rows: filteredTableData,
        isLoading,
        showEditDialog,
        setShowEditDialog,
        showDeleteDialog,
        setShowDeleteDialog,
        refetchOverview: refetch,
        openActivity,
        responsibleOptions: responsibleQuery.userData,
        responsiblesIsLoading: responsibleQuery.isLoading,
        getFilterDataOptions,
        onFiltersChanged,
        clearFilters,
        hasActiveFilters,
        searchString,
        setSearchString,
        onAssigneesChange,
        onResponsibleChange,
        hasControls: isLoading || (data && data!.rows.length > 0),
        showAddAnnualWheelActivityDialog,
        setShowAddAnnualWheelActivityDialog,
        taskForDeletion,
        setTaskForDeletion,
        showAddFrameworkControlTemplateAnnualWheelActivity,
        setShowAddFrameworkControlTemplateAnnualWheelActivity,
        onAddedAnnualWheelActivityTemplates,
    };

    function formatDataToTable(data: Array<FrameworkControlTaskRow>): Array<FrameworkControlTaskTableRow> {
        return data.map((task) => {
            return {
                ...task,
                assigneesAsString: task.assignees.map((x) => x.name).join(", "),
                deadlineAsString: translateDateWithFullMonthAndYear(task.deadline),
                statusAsString: translateFrameworkControlStatusEnum(task.status.toString()),
                responsibleAsString: task.responsible?.name,
                evaluationStatusAsString: task.evaluationStatus ? translateEvaluationEnum(task.evaluationStatus.toString()) : "",
            };
        });
    }

    function getFilterDataOptions(): Array<FilterOption> {
        // Every option must carry fixedData. An option without it is treated as server resolved, and the
        // /filter-data endpoint knows none of these filter ids, which would leave the chips without names.
        if (!data) {
            return [];
        }

        const filterOptions: Array<FilterOption> = [
            {
                type: FilterOptionType.SelectField,
                id: "deadline",
                value: selectFilters.deadline ?? [],
                label: translateString("deadline"),
                fixedData: deadlineOptions,
            },
            {
                type: FilterOptionType.SelectField,
                id: "responsibleOrAssignee",
                value: selectFilters.responsibleOrAssignee ?? [],
                label: translateString("responsibleOrAssignee"),
                fixedData: data.userOptions,
            },
            {
                type: FilterOptionType.SelectField,
                id: "status",
                value: selectFilters.status ?? [],
                label: translateString("status"),
                fixedData: statusOptions,
            },
        ];

        return withOperators(filterOptions, filterOperators);
    }

    function onFiltersChanged(filterOptions: Array<FilterOption>) {
        // getFilterDataOptions yields nothing until the overview has loaded, so closing the popover in that
        // window would otherwise persist an empty snapshot over the stored selection.
        if (filterOptions.length === 0) {
            return;
        }

        onFilterChange(filterOptions);
    }

    // A selection whose id has disappeared from its option list renders no chip while still filtering rows away,
    // so resetting everything is the only way back out.
    function clearFilters() {
        onFilterChange(getFilterDataOptions().map((option) => ({ ...option, value: [] })));
    }

    async function updateResponsible(data: { taskId: string; responsibleId: string }) {
        return await put<string>(`/task/${data.taskId}/updateResponsible/`, data.responsibleId);
    }

    async function updateAssignee(data: { taskId: string; assigneeIds: Array<string> }) {
        return await put<string>(`/task/${data.taskId}/updateAssignees/`, data.assigneeIds);
    }
}
